import React, { useEffect, useMemo } from 'react';
import { parse as queryParse } from 'querystring';
import { useHistory, useLocation } from 'react-router';
import { useAppConfiguration } from '../../../AppConfiguration';
import { useCategories, useInfiniteEvents } from '../../../api/hooks';
import { generateRoute, useRoutes } from '../../../routing/RouteProvider';
import Loader from '../Loader';
import Error from '../Error';
import {objRemoveEmptyAndFalseyProperties, isJsonParsable} from '../../../helper';
import EventsListingHeader from './events/EventsListingHeader';
import EventsListingMain from './events/EventsListingMain';
import setFilterOperations from '../../../util/setFilterOperations';
import {dateRangeTypes} from '../../filter/fields/DateRangePicker';
import useSessionStorage from '../../../util/useSessionStorage';

const EventsContent = ({customFilter = false}) => {
    // Get filter
    const {search} = useLocation();
    const history = useHistory();

    let {eventsFilterConfiguration, fixedFilters: fixedFiltersTemp, defaultListingsView} = useAppConfiguration();
    const fixedFilters = useMemo(() => JSON.parse(JSON.stringify(fixedFiltersTemp)), [fixedFiltersTemp]);

    // Get the tags for the customer to choose from
    const tags = eventsFilterConfiguration.tags || [
        'Geeignet für Familien und Kinder',
        'Barrierefrei',
    ];

    // Get the filter query from the url
    const {filter: filterString} = queryParse(search.substr(1));

    // Check if the filter is from the api
    const isCustomFilter = customFilter ? Object.values(customFilter).find(({slug}) => slug === filterString) : false;

    const {filterConfiguration} = useAppConfiguration();
    const dateDefault = filterConfiguration.dateOption?.default || 'arbitrary';
    const isValidDefaultDate = dateDefault in dateRangeTypes;

    // If not, use the set query instead or the default filter if no filter is set at all
    let filter = useMemo(() => (
        filterString &&
        !isCustomFilter &&
        isJsonParsable(filterString)) &&
        Object.keys(JSON.parse(filterString)).length > 0
        ? JSON.parse(filterString) : {
        // By default show 'arbitrary' events if not defined in the configuration
            'end_date_from': isValidDefaultDate ? dateRangeTypes[dateDefault][0] : dateRangeTypes['arbitrary'][0],
            'start_date_to': isValidDefaultDate ? dateRangeTypes[dateDefault][1] : dateRangeTypes['arbitrary'][1],
            // Include fixed filter in the default filter
            ...setFilterOperations(fixedFilters, {
                'tags': 'AND',
            }),
        }, [filterString, isCustomFilter, isValidDefaultDate, dateDefault, fixedFilters]
    );

    filter.end_date_from || delete filter.end_date_from;
    filter.start_date_to || delete filter.start_date_to;

    // If the filter is from the api, apply the correct filter options
    if (isCustomFilter) {
        const backendQuery = JSON.parse(JSON.stringify(isCustomFilter.query));

        // We transform the "tags" key into "tags_title" for events, but in this case tags actually contains the tag
        // ids.
        if (backendQuery.tags) {
            backendQuery.tags_id = backendQuery.tags;
            delete backendQuery.tags;
        }

        filter = {...backendQuery, ...setFilterOperations(fixedFilters, {
            'tags': 'AND',
        })};
    }

    // Merge searched and fixed tags together
    if (Array.isArray(filter.tags) && Array.isArray(fixedFilters.tags)) {
        filter.tags = [...new Set([...filter.tags ,...fixedFilters.tags])];
        filter = setFilterOperations(filter, {
            'tags': 'AND',
        });
    }

    // Set filter function
    const routes = useRoutes();
    function setFilter(filter) {
        const {tags: fixedTags, categories: fixedCategories, ...fixedFilterRest} = fixedFilters;

        // Add fixed tags
        if (fixedTags && Array.isArray(fixedTags)) {
            if (!filter.tags) {
                filter.tags = fixedTags;
            } else {
                fixedTags.map(tag =>
                    !filter.tags.includes(tag) && filter.tags.push(tag)
                );
            }
        }

        // Add fixed categories
        if (fixedCategories && Array.isArray(fixedCategories)) {
            if (!filter.categories) {
                filter.categories = fixedCategories;
            } else {
                fixedCategories.map(category =>
                    !filter.categories.includes(category) ?? filter.categories.push(category)
                );
            }
        }

        // Add all other fixed filters
        filter = {...filter, ...fixedFilterRest};

        const url = generateRoute(routes, 'events', {
            filter: setFilterOperations(filter, {
                'tags': 'AND',
            }),
        });

        history.push(url);
    }

    // Set the list type for the event listing
    if (defaultListingsView !== 'grid' && defaultListingsView !== 'list' && defaultListingsView !== 'map') {
        defaultListingsView = 'grid';
    }
    const [listType, setListType] = useSessionStorage('eventsListingView', defaultListingsView);
    useEffect(() => {
        // Add global listing-map-view class to body, if map view is enabled. Gets used to change some styles on mobile
        // when the map view is enabled to make the page behave more app-like.
        document.body.classList.toggle('listing-map-view', listType === 'map');

        return () => document.body.classList.remove('listing-map-view');
    }, [listType]);

    // Fetch events from the api
    const {
        data: eventGroups,
        isLoading: eventsLoading,
        error: eventsError,
        fetchMore,
        canFetchMore,
        isFetchingMore
    } = useInfiniteEvents(filter);
    const {data: categories, isLoading: categoriesLoading, error: categoriesError} = useCategories();

    if (eventsLoading || categoriesLoading) {
        return <Loader />;
    }

    if (categoriesError || eventsError) {
        return <Error errors={[categoriesError && categoriesError, eventsError && eventsError]} />;
    }

    // Function to run on filter submit with chosen options
    const onSubmit = newFilter => {

        // Update tags
        const tagsTitle = [];
        // Allow adding tags from the normal tag list and from the tag dropdown (if it is set)
        for (const tag of new Set([...tags, ...(eventsFilterConfiguration.tagDropdown?.tags || [])])) {
            if (newFilter[tag]) {
                tagsTitle.push(tag);
                delete newFilter[tag];
            }
        }

        // Add additional tags if set (these are hidden to the user)
        for (const tag of newFilter.additionalTags || []) {
            tagsTitle.push(tag);
        }
        delete newFilter.additionalTags;

        if (tagsTitle.length > 0) {
            newFilter.tags = tagsTitle;
        }

        // Remove empty properties
        objRemoveEmptyAndFalseyProperties(newFilter);

        setFilter(newFilter);
    };

    return (
        <section className={`all-listing-wrapper section-bg ${listType === 'map' ? 'remove-bottom-padding' : ''}`}>
            <div className="container">
                <div className="row">
                    {/* Events Header */}
                    <div className="col-lg-12 listing-header-container">
                        <EventsListingHeader
                            events={eventGroups[0]}
                            listType={listType}
                            setListType={setListType}
                            categories={categories}
                            tags={tags}
                            onSubmit={onSubmit}
                            filter={filter}
                            fixedFilter={fixedFilters}
                        />
                    </div>

                    {/* Events Listing */}
                    <div className="col-lg-12 mt-0 listing-items">
                        <EventsListingMain
                            eventGroups={eventGroups}
                            fetchMore={fetchMore}
                            canFetchMore={canFetchMore}
                            isFetchingMore={isFetchingMore}
                            categories={categories}
                            tags={tags}
                            onSubmit={onSubmit}
                            listType={listType}
                            setListType={setListType}
                            filter={filter}
                            fixedFilter={fixedFilters}
                        />
                    </div>
                </div>
            </div>
        </section>
    );
};

export default EventsContent;
